/**
 * Sprint TC-D.1 — Activity Timeline Panel.
 *
 * Read-only vertical timeline of every meaningful event in a recruitment
 * process: stage changes, internal notes, candidate additions, assessment
 * submissions, report lifecycle, decision changes, shortlist toggles, and
 * the process_closed event. Advisor-only — the backend endpoint is gated
 * to hm_admin / hm_advisor.
 *
 * Pure consumer: fetches `/admin/recruitment-processes/{pid}/activity`,
 * normalizes the bilingual labels, renders. No mutations.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Activity, RefreshCw, ChevronDown, ChevronUp,
  GitBranch, MessageCircle, UserPlus, Send, FileText, ClipboardCheck,
  ShieldCheck, Sparkles, ScanLine, Star, StarOff, XCircle, Filter,
} from 'lucide-react';
import { recruitmentAdminApi } from '@/lib/recruitmentApi';
import { formatApiError } from '@/lib/api';
import { clientOutcomeLabel } from '@/lib/assignmentState';

// P0 UAT 2026-08-11 — render the structured `closed_reason` audit value as a
// localized, human-readable description. The stored event is preserved as-is;
// only the presentation is corrected. Unknown/legacy values fall back to a
// humanized rendering (never the raw internal key with colons/underscores).
function closedReasonLabel(reason, isES) {
  if (!reason) return '';
  const m = /^client_outcome:(.+)$/.exec(reason);
  if (m) {
    const label = clientOutcomeLabel(m[1], isES);
    return (isES ? 'Resultado del cliente: ' : 'Client outcome: ') + label;
  }
  return reason.toString().replace(/[:]/g, ' — ').replace(/_/g, ' ');
}
import { toast } from 'sonner';

const KIND_META = {
  stage_change:        { Icon: GitBranch,      tone: 'bg-primary/10 text-primary border-primary/20' },
  note_added:          { Icon: MessageCircle,  tone: 'bg-muted text-foreground border-muted-foreground/20' },
  candidate_added:     { Icon: UserPlus,       tone: 'bg-info/10 text-info border-info/20' },
  assessment_submitted:{ Icon: Send,           tone: 'bg-success/10 text-success border-success/20' },
  report_draft:        { Icon: ScanLine,       tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  report_reviewed:     { Icon: FileText,       tone: 'bg-blue-100 text-blue-700 border-blue-200' },
  report_approved:     { Icon: ClipboardCheck, tone: 'bg-blue-100 text-blue-700 border-blue-200' },
  report_released:     { Icon: ShieldCheck,    tone: 'bg-success/10 text-success border-success/20' },
  decision_changed:    { Icon: Sparkles,       tone: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  shortlist_added:     { Icon: Star,           tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  shortlist_removed:   { Icon: StarOff,        tone: 'bg-muted text-foreground border-muted-foreground/20' },
  process_closed:      { Icon: XCircle,        tone: 'bg-red-100 text-red-700 border-red-200' },
};

const FILTERABLE_KINDS = Object.keys(KIND_META);

function fmtDateTime(iso, lang) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(
      (lang || '').startsWith('es') ? 'es-ES' : 'en-US',
      { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    );
  } catch { return iso; }
}

function EventRow({ ev, i18n, t, viewerRole = 'admin' }) {
  const meta = KIND_META[ev.kind] || { Icon: Activity, tone: 'bg-muted' };
  const Icon = meta.Icon;
  const isES = (i18n.language || '').startsWith('es');

  const title = (() => {
    switch (ev.kind) {
      case 'stage_change':
        return (isES ? 'Etapa: ' : 'Stage: ') + (ev.details?.label?.[isES ? 'es' : 'en'] || `#${ev.details?.stage}`);
      case 'note_added':
        return isES ? 'Nota interna' : 'Internal note';
      case 'candidate_added':
        return (isES ? 'Candidato añadido' : 'Candidate added') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'assessment_submitted':
        return (isES ? 'Evaluación enviada' : 'Assessment submitted') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'report_draft':
        return (isES ? 'Borrador IA generado' : 'AI draft generated') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'report_reviewed':
        return (isES ? 'Informe revisado' : 'Report reviewed') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'report_approved':
        return (isES ? 'Informe aprobado' : 'Report approved') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'report_released':
        return (isES ? 'Informe entregado al cliente' : 'Report released to client') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'decision_changed': {
        const label = t(`recruitment.candidates.decision.${ev.details?.status}`, {
          defaultValue: (ev.details?.status || '').replace(/_/g, ' '),
        });
        return (isES ? 'Decisión: ' : 'Decision: ') + label + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      }
      case 'shortlist_added':
        return (isES ? 'Añadido a la preselección' : 'Added to shortlist') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'shortlist_removed':
        return (isES ? 'Removido de la preselección' : 'Removed from shortlist') + (ev.candidate_name ? ` — ${ev.candidate_name}` : '');
      case 'process_closed':
        return isES ? 'Proceso cerrado' : 'Process closed';
      default:
        return ev.kind;
    }
  })();

  return (
    <li
      className="relative pl-9 pr-1 pb-4 last:pb-0"
      data-testid={`activity-event-${ev.kind}`}
    >
      <span className={`absolute left-1 top-0.5 h-6 w-6 rounded-full grid place-items-center border ${meta.tone}`}>
        <Icon className="h-3 w-3" />
      </span>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-medium text-sm leading-tight">{title}</p>
        <time className="text-xs tabular-nums text-muted-foreground shrink-0">
          {fmtDateTime(ev.timestamp, i18n.language)}
        </time>
      </div>
      {(ev.actor_name || ev.actor_role) && (
        <p className="text-xs text-muted-foreground mt-0.5">
          {ev.actor_name || t(`pages.users.roles.${ev.actor_role}`, { defaultValue: ev.actor_role })}
          {ev.actor_name && ev.actor_role ? ` · ${t(`pages.users.roles.${ev.actor_role}`, { defaultValue: ev.actor_role })}` : ''}
        </p>
      )}
      {ev.kind === 'note_added' && ev.details?.body && (
        <p className="text-xs italic text-foreground/80 mt-1 line-clamp-3" title={ev.details.body}>
          “{ev.details.body}”
        </p>
      )}
      {ev.kind === 'decision_changed' && ev.details?.note && (
        <p className="text-xs italic text-muted-foreground mt-1 line-clamp-3" title={ev.details.note}>
          “{ev.details.note}”
        </p>
      )}
      {ev.kind === 'process_closed' && ev.details?.reason && (
        <p className="text-xs italic text-muted-foreground mt-1">
          “{closedReasonLabel(ev.details.reason, isES)}”
        </p>
      )}
      {ev.details?.assignment_id && viewerRole === 'admin' && (
        <p className="mt-1">
          <Link
            to={`/admin/evaluations/${ev.details.assignment_id}`}
            className="text-xs text-primary hover:underline"
          >
            {(i18n.language || '').startsWith('es') ? 'Ver evaluación →' : 'View assignment →'}
          </Link>
        </p>
      )}
      {/* A8 (limited Phase 2) — clients never receive admin routes: released
          reports link to the authorized client library; otherwise explain. */}
      {ev.details?.assignment_id && viewerRole !== 'admin' && (
        <p className="mt-1">
          {ev.kind === 'report_released' ? (
            <Link
              to="/client/reports"
              className="text-xs text-primary hover:underline"
              data-testid="activity-client-reports-link"
            >
              {t('recruitment.activity.viewInReports', { defaultValue: isES ? 'Ver en Informes →' : 'View in Reports →' })}
            </Link>
          ) : null}
        </p>
      )}
    </li>
  );
}

export default function ActivityTimelinePanel({ processId, fetcher, viewerRole = 'admin' }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [kindFilter, setKindFilter] = useState([]); // empty == show all
  const fetchFn = fetcher || recruitmentAdminApi.getActivity;

  const load = async () => {
    if (!processId) return;
    setLoading(true);
    try {
      const d = await fetchFn(processId);
      setData(d);
    } catch (e) {
      toast.error(
        isES ? 'No se pudo cargar la actividad' : 'Could not load activity',
        { description: formatApiError(e) },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [processId]);

  // Reverse to descending (newest first) for a more useful default view.
  const events = useMemo(() => {
    const list = data?.events ? [...data.events].reverse() : [];
    if (kindFilter.length === 0) return list;
    return list.filter((e) => kindFilter.includes(e.kind));
  }, [data, kindFilter]);

  const counts = useMemo(() => {
    const map = {};
    (data?.events || []).forEach((e) => { map[e.kind] = (map[e.kind] || 0) + 1; });
    return map;
  }, [data]);

  return (
    <Card className="p-5 shadow-card" data-testid="activity-timeline">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Activity className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">
          {isES ? 'Historial del proceso' : 'Process activity'}
        </h3>
        {data && (
          <Badge variant="outline" className="ml-1 text-xs tabular-nums" data-testid="activity-count">
            {data.event_count}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm" variant="ghost"
            onClick={load} disabled={loading}
            className="h-7 gap-1.5"
            data-testid="activity-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-xs">{isES ? 'Actualizar' : 'Refresh'}</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => setCollapsed((v) => !v)}
            className="h-7 gap-1.5"
            data-testid="activity-collapse-toggle"
          >
            {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            <span className="text-xs">
              {collapsed
                ? (isES ? 'Mostrar' : 'Show')
                : (isES ? 'Ocultar' : 'Hide')}
            </span>
          </Button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Kind filter chips */}
          {data && data.event_count > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="activity-kind-filter">
              <Filter className="h-3 w-3 text-muted-foreground" />
              {FILTERABLE_KINDS.filter((k) => counts[k]).map((k) => {
                const active = kindFilter.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKindFilter((prev) => active ? prev.filter((x) => x !== k) : [...prev, k])}
                    className={`px-2 py-0.5 rounded-full border text-xs font-medium transition-colors ${
                      active
                        ? 'bg-primary/10 border-primary/30 text-primary'
                        : 'border-input text-muted-foreground hover:bg-muted/40'
                    }`}
                    data-testid={`activity-kind-chip-${k}`}
                  >
                    {t(`recruitment.activity.kind.${k}`, {
                      defaultValue: k.replace(/_/g, ' '),
                    })}
                    <span className="ml-1 tabular-nums opacity-70">{counts[k]}</span>
                  </button>
                );
              })}
              {kindFilter.length > 0 && (
                <button
                  type="button"
                  onClick={() => setKindFilter([])}
                  className="px-2 py-0.5 rounded-full border text-xs font-medium text-muted-foreground border-input hover:bg-muted/40"
                  data-testid="activity-kind-clear"
                >
                  {isES ? 'Limpiar' : 'Clear'}
                </button>
              )}
            </div>
          )}

          {loading && (
            <div className="space-y-2" data-testid="activity-loading">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          )}

          {!loading && data && events.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="activity-empty">
              {kindFilter.length > 0
                ? (isES ? 'Ningún evento coincide con el filtro.' : 'No events match the current filter.')
                : (isES ? 'Aún no hay actividad registrada.' : 'No activity recorded yet.')}
            </p>
          )}

          {!loading && events.length > 0 && (
            <ol
              className="relative border-l border-border ml-3 mt-1 space-y-1"
              data-testid="activity-list"
            >
              {events.map((ev, i) => (
                <EventRow key={`${ev.kind}-${ev.timestamp}-${i}`} ev={ev} i18n={i18n} t={t} viewerRole={viewerRole} />
              ))}
            </ol>
          )}
        </>
      )}
    </Card>
  );
}
